/**
 * Explore Store
 * Global state management for constellation-based music discovery
 */

import { create } from 'zustand';
import type {
  ConstellationNode,
  VibeControls,
  ExploreAction,
  ExploreResponse,
  SavedConstellation,
  PlatformTab,
  CoherenceLevel,
} from '../types/explore.types';
import type { EnrichedTrack } from '../types/track.types';

interface ExploreStore {
  // Constellation state
  nodes: ConstellationNode[];
  vibe: VibeControls;
  currentConstellationId: string | null;
  savedConstellations: SavedConstellation[];

  // Results state
  exploreId: string | null;
  interpretation: string | null;
  results: EnrichedTrack[];
  suggestedNodes: ConstellationNode[];
  coherenceScore: number;
  activeTab: PlatformTab;

  // UI state
  loading: boolean;
  error: string | null;
  cacheHit: boolean;
  responseTimeMs: number;

  // Actions - Constellation Management
  addNode: (node: ConstellationNode) => void;
  removeNode: (index: number) => void;
  updateNodeWeight: (index: number, weight: number) => void;
  setVibe: (vibe: Partial<VibeControls>) => void;
  clearConstellation: () => void;
  loadConstellation: (constellation: SavedConstellation) => void;

  // Actions - Saved Constellations
  setSavedConstellations: (constellations: SavedConstellation[]) => void;
  addSavedConstellation: (constellation: SavedConstellation) => void;
  deleteSavedConstellation: (id: string) => void;

  // Actions - Results
  setExploreResults: (response: ExploreResponse) => void;
  clearResults: () => void;
  setActiveTab: (tab: PlatformTab) => void;

  // Actions - UI
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed
  isValid: () => boolean;
  getCoherenceLevel: () => CoherenceLevel;
  getYouTubeResults: () => EnrichedTrack[];
  getBandcampResults: () => EnrichedTrack[];
}

/**
 * Default vibe controls (neutral/balanced)
 */
const DEFAULT_VIBE: VibeControls = {
  groove: 0.5,
  temperature: 0.5,
  obscurity: 0.5,
  density: 0.5,
};

/**
 * Explore store using Zustand
 */
export const useExploreStore = create<ExploreStore>((set, get) => ({
  // Initial state
  nodes: [],
  vibe: DEFAULT_VIBE,
  currentConstellationId: null,
  savedConstellations: [],

  exploreId: null,
  interpretation: null,
  results: [],
  suggestedNodes: [],
  coherenceScore: 0,
  activeTab: 'youtube',

  loading: false,
  error: null,
  cacheHit: false,
  responseTimeMs: 0,

  /**
   * Add a node to the constellation
   */
  addNode: (node) => {
    const { nodes } = get();
    // Avoid duplicate nodes with same type and value
    const exists = nodes.some((n) => n.type === node.type && n.value === node.value);
    if (!exists) {
      set({ nodes: [...nodes, node] });
    }
  },

  /**
   * Remove a node by index
   */
  removeNode: (index) => {
    const { nodes } = get();
    set({ nodes: nodes.filter((_, i) => i !== index) });
  },

  /**
   * Update node weight
   */
  updateNodeWeight: (index, weight) => {
    const { nodes } = get();
    const updatedNodes = nodes.map((node, i) =>
      i === index ? { ...node, weight: Math.max(0, Math.min(1, weight)) } : node
    );
    set({ nodes: updatedNodes });
  },

  /**
   * Set vibe controls (partial update)
   */
  setVibe: (vibeUpdate) => {
    const { vibe } = get();
    // Clamp values to 0-1 range
    const clampedVibe = Object.entries(vibeUpdate).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: Math.max(0, Math.min(1, value)),
      }),
      {}
    );
    set({ vibe: { ...vibe, ...clampedVibe } });
  },

  /**
   * Clear constellation (reset to empty state)
   */
  clearConstellation: () => {
    set({
      nodes: [],
      vibe: DEFAULT_VIBE,
      currentConstellationId: null,
      results: [],
      interpretation: null,
      suggestedNodes: [],
      coherenceScore: 0,
      error: null,
    });
  },

  /**
   * Load a saved constellation
   */
  loadConstellation: (constellation) => {
    set({
      nodes: constellation.nodes,
      vibe: constellation.vibe,
      currentConstellationId: constellation.id,
      results: [],
      interpretation: null,
      error: null,
    });
  },

  /**
   * Set saved constellations list
   */
  setSavedConstellations: (constellations) => {
    set({ savedConstellations: constellations });
  },

  /**
   * Add a saved constellation to the list
   */
  addSavedConstellation: (constellation) => {
    const { savedConstellations } = get();
    set({ savedConstellations: [constellation, ...savedConstellations] });
  },

  /**
   * Delete a saved constellation
   */
  deleteSavedConstellation: (id) => {
    const { savedConstellations, currentConstellationId } = get();
    set({
      savedConstellations: savedConstellations.filter((c) => c.id !== id),
      // Clear current if we're deleting the loaded constellation
      currentConstellationId: currentConstellationId === id ? null : currentConstellationId,
    });
  },

  /**
   * Set explore results from API response
   */
  setExploreResults: (response) => {
    set({
      exploreId: response.data.explore_id,
      interpretation: response.data.interpretation,
      results: response.data.recommendations,
      suggestedNodes: response.data.suggested_nodes,
      coherenceScore: response.data.coherence_score,
      cacheHit: response.data.cache_hit,
      responseTimeMs: response.data.response_time_ms,
      loading: false,
      error: null,
    });
  },

  /**
   * Clear results
   */
  clearResults: () => {
    set({
      exploreId: null,
      interpretation: null,
      results: [],
      suggestedNodes: [],
      coherenceScore: 0,
      cacheHit: false,
      responseTimeMs: 0,
      error: null,
    });
  },

  /**
   * Set active platform tab
   */
  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  /**
   * Set loading state
   */
  setLoading: (loading) => {
    set({ loading });
  },

  /**
   * Set error message
   */
  setError: (error) => {
    set({ error, loading: false });
  },

  /**
   * Check if constellation is valid (≥3 nodes)
   */
  isValid: () => {
    return get().nodes.length >= 3;
  },

  /**
   * Get coherence level label
   */
  getCoherenceLevel: (): CoherenceLevel => {
    const { coherenceScore } = get();
    if (coherenceScore < 0.4) return 'wild';
    if (coherenceScore > 0.7) return 'tight';
    return 'balanced';
  },

  /**
   * Get results filtered to YouTube availability
   */
  getYouTubeResults: () => {
    return get().results.filter((track) => track.youtube_url);
  },

  /**
   * Get results filtered to Bandcamp availability
   */
  getBandcampResults: () => {
    return get().results.filter((track) => track.bandcamp_url);
  },
}));
